import React from 'react';
import type { MusicalToken, RhythmReadingMethod, TimeSignature } from '../../types/music';
import { tokenToLabel, tokenToSymbol } from '../tokens';
import { VexRhythmGlyph } from './VexRhythmGlyph';
import { VexRhythmStrip } from './VexRhythmStrip';
import type { RhythmSlotFeedback } from '../../types/payloads/rhythm_canvas.types';

const TOKEN_OPTIONS: MusicalToken[] = ['W', 'H', 'Q', 'E', 'S', 'R_Q'];

function paletteFromPattern(pattern: MusicalToken[]): MusicalToken[] {
  return [...new Set([...pattern, ...TOKEN_OPTIONS])];
}

export interface RhythmWorkshopGridProps {
  pattern: MusicalToken[];
  timeSignature?: TimeSignature;
  readingMethod?: RhythmReadingMethod;
  taskKind: 'complete' | 'transcribe' | 'syllables' | 'practice' | 'identify';
  blankIndices: number[];
  missingIndex?: number;
  dottedIndices?: number[];
  targetBarNumber?: number;
  graphicMode?: 'symbols' | 'symbols-labels';
  hidePattern?: boolean;
  values: Record<string, string>;
  feedback?: Record<string, RhythmSlotFeedback>;
  disabled?: boolean;
  onSlotChange: (index: number, value: string) => void;
}

export function RhythmWorkshopGrid({
  pattern,
  timeSignature = '4/4',
  readingMethod = 'tradicional',
  taskKind,
  blankIndices,
  missingIndex,
  dottedIndices = [],
  targetBarNumber,
  graphicMode = 'symbols',
  hidePattern = false,
  values,
  feedback,
  disabled = false,
  onSlotChange,
}: RhythmWorkshopGridProps) {
  const palette = paletteFromPattern(pattern);
  const isSyllables = taskKind === 'syllables';
  const showStrip = !hidePattern && taskKind !== 'transcribe';
  const stripMissing =
    taskKind === 'complete' ? missingIndex : taskKind === 'transcribe' ? undefined : undefined;

  const transcribeStripMissing =
    taskKind === 'transcribe'
      ? blankIndices.length === pattern.length
        ? undefined
        : blankIndices[0]
      : stripMissing;

  return (
    <div className="rhythm-workshop">
      {showStrip && (
        <VexRhythmStrip
          pattern={pattern}
          timeSignature={timeSignature}
          missingIndex={
            taskKind === 'syllables'
              ? undefined
              : taskKind === 'complete'
                ? missingIndex
                : transcribeStripMissing
          }
          showLabels={graphicMode === 'symbols-labels'}
          readingMethod={readingMethod}
          targetBarNumber={targetBarNumber}
          dottedIndices={dottedIndices}
        />
      )}

      {hidePattern && (
        <p className="rhythm-workshop__dictation-hint">
          Escucha el patrón y escribe las figuras en los casilleros.
        </p>
      )}

      <div className="rhythm-workshop__grid" role="group" aria-label="Casilleros rítmicos">
        {pattern.map((token, index) => {
          const isBlank = blankIndices.includes(index);
          const slotKey = String(index);
          const value = values[slotKey] ?? '';
          const slotFeedback = feedback?.[slotKey];
          const expectedLabel = tokenToLabel(token, readingMethod);

          if (!isBlank && taskKind !== 'syllables') {
            return (
              <div key={index} className="rhythm-workshop__cell rhythm-workshop__cell--given">
                <span className="rhythm-workshop__cell-num">{index + 1}</span>
                <VexRhythmGlyph token={token} size={32} color="#e8e4dc" />
                {graphicMode === 'symbols-labels' && (
                  <span className="rhythm-workshop__cell-label">{expectedLabel}</span>
                )}
              </div>
            );
          }

          return (
            <div
              key={index}
              className={`rhythm-workshop__cell rhythm-workshop__cell--input${
                slotFeedback === 'correct'
                  ? ' rhythm-workshop__cell--correct'
                  : slotFeedback === 'wrong'
                    ? ' rhythm-workshop__cell--wrong'
                    : ''
              }`}
            >
              <span className="rhythm-workshop__cell-num">{index + 1}</span>
              {isSyllables ? (
                <>
                  <VexRhythmGlyph token={token} size={28} color="#e8e4dc" />
                  <input
                    type="text"
                    className="rhythm-workshop__syllable-input"
                    disabled={disabled}
                    value={value}
                    placeholder="Sílaba"
                    aria-label={`Sílaba figura ${index + 1}`}
                    onChange={(e) => onSlotChange(index, e.target.value)}
                  />
                  {!disabled && slotFeedback && (
                    <span className="rhythm-workshop__expected">{expectedLabel}</span>
                  )}
                </>
              ) : (
                <>
                  <select
                    className="rhythm-workshop__token-select"
                    disabled={disabled}
                    value={normalizeTokenValue(value) || ''}
                    aria-label={`Figura casillero ${index + 1}`}
                    onChange={(e) => onSlotChange(index, e.target.value)}
                  >
                    <option value="">?</option>
                    {palette.map((opt) => (
                      <option key={opt} value={opt}>
                        {tokenToSymbol(opt)} {tokenToLabel(opt, readingMethod)}
                      </option>
                    ))}
                  </select>
                  {!disabled && slotFeedback === 'wrong' && (
                    <span className="rhythm-workshop__expected">{tokenToLabel(token, readingMethod)}</span>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {!disabled && (
        <div className="rhythm-workshop__palette" aria-label="Paleta de figuras">
          <span className="rhythm-workshop__palette-label">Figuras:</span>
          {TOKEN_OPTIONS.filter((t) => palette.includes(t)).map((tok) => (
            <button
              key={tok}
              type="button"
              className="rhythm-workshop__palette-btn"
              title={tokenToLabel(tok, readingMethod)}
              disabled={disabled}
              onClick={() => {
                const nextBlank = blankIndices.find((i) => !values[String(i)]);
                if (nextBlank !== undefined) onSlotChange(nextBlank, tok);
              }}
            >
              <VexRhythmGlyph token={tok} size={24} color="#e8e4dc" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function normalizeTokenValue(raw: string): MusicalToken | '' {
  const v = String(raw || '').trim().toUpperCase();
  if (TOKEN_OPTIONS.includes(v as MusicalToken)) return v as MusicalToken;
  return '';
}
